
import moment, { Moment } from 'moment';
import { Box, Button, CircularProgress, createFilterOptions, Typography } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useNavigate } from "react-router-dom";
import styles from "../../assets/styles/form.module.css";
import { useCallback, useEffect, useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import { handleApiError } from "../../utils/errorHandler";
import { BTN_DONE, CHOOSE_PROGRAM, CHOOSE_SCHOOL, COMPLETE_PROFILE, E_CONFIRM_PASSWORD, E_ENTER_PASSWORD, E_PASSWORD_NOT_MATCHED, E_REQUIRED_BIRTHDATE, E_REQUIRED_FIRST_NAME, E_REQUIRED_LAST_NAME } from "../../utils/constants";
import PasswordAdornment from "../../components/common/PasswordAdornment/PasswordAdornment";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { blue, grey } from "@mui/material/colors";
import { profile, updateProfile } from "../../api/services/user";
import { UserProfile } from "../../api/interfaces/User";
import PinnacleAutocomplete from '../../components/PinnacleAutocomplete/PinnacleAutocomplete';
import { getSchools } from '../../api/services/school';
import { School } from '../../api/interfaces/School';
import { getPrograms } from '../../api/services/program';
import { Program } from '../../api/interfaces/Program';
import { FieldSkeletonWrapper } from '../../components/skeleton/SkeletonAccountSetup/SkeletonAccountSetup';
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';
import { StyledPinnacleDatePicker } from '../../components/PinnacleDatePicker/PinnacleDatePicker.styled';
import PinnacleFormContainer from '../../components/PinnacleFormContainer/PinnacleFormContainer';

export default function AccountSetupPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();

    // Api response state
    const [currentProfile, setCurrentProfile] = useState<null | UserProfile>(null);
    const [schoolsOption, setSchoolsOption] = useState<null | School[]>(null);
    const [programsOption, setProgramsOption] = useState<null | Program[]>(null);

    // Loader
    const [profileLoading, setProfileLoading] = useState(true);
    const [schoolsLoading, setSchoolsLoading] = useState(true);
    const [programsLoading, setProgramsLoading] = useState(true);

    const [formDisabled, setFormDisabled] = useState<boolean>(false);

    // First Name
    const [firstName, setFirstName] = useState('');
    const [firstNameTouched, setFirstNameTouched] = useState(false);
    const isFirstNameValid = (firstName ?? '').trim().length > 0;

    // Last Name
    const [lastName, setLastName] = useState('');
    const [lastNameTouched, setLastNameTouched] = useState(false);
    const isLastNameValid = lastName.trim().length > 0;

    // Birthdate
    const [birthDate, setBirthDate] = useState<Moment | null>(null);
    const [birthDateTouched, setBirthDateTouched] = useState(false);
    const isBirthDateValid = !!birthDate;

    // Calculate max date: today minus 5 years
    const maxAllowedDate = moment().subtract(5, 'years');

    // Email
    const [email, setEmail] = useState<string>('');

    // School
    const [school, setSchool] = useState('');
    const [schoolTouched, setSchoolTouched] = useState(false);

    // Programs
    const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
    const [selectedProgramTouched, setSelectedProgramTouched] = useState(false);

    // Password
    const [password, setPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const isPasswordValid = password.length > 0;

    // Confirm Password
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState<boolean>(false);
    const isConfirmPasswordValid = confirmPassword.length > 0;


    const fetchProfile = useCallback(async () => {
        setProfileLoading(true);
        try {
            const response = await profile();
            const user = response.data.user;

            if (user.isEula) {
                navigate('/');
            } else {
                setCurrentProfile(response.data);
                setFirstName(user?.firstName ?? '');
                setLastName(user?.lastName ?? '');
                setBirthDate(user?.birthDate ? moment(user.birthDate) : null);
                setEmail(user?.email ?? '');
                setSchool(user?.school ?? '');
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setProfileLoading(false);
        }
    }, [navigate]);

    const fetchSchool = useCallback(async () => {
        setSchoolsLoading(true);
        try {
            const response = await getSchools();
            setSchoolsOption(response.data)
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setSchoolsLoading(false);
        }
    }, []);

    const fetchPrograms = useCallback(async () => {
        setProgramsLoading(true);
        try {
            const response = await getPrograms();
            setProgramsOption(response.data)
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setProgramsLoading(false);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            await fetchProfile();
            await fetchSchool();
        }
        init();
    }, [fetchProfile, fetchSchool]);

    useEffect(() => {
        if (!currentProfile?.program) {
            fetchPrograms();
        }
    }, [currentProfile?.program, fetchPrograms]);

    useEffect(() => {
        if (currentProfile && programsOption && programsOption.length > 0) {
            const match = programsOption.find((p: Program) => p._id === currentProfile.user.programId);
            setSelectedProgram(match ?? null);
        }
    }, [currentProfile, programsOption]);

    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(e.target.value);
        if (!firstNameTouched) setFirstNameTouched(true);
    };

    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(e.target.value);
        if (!lastNameTouched) setLastNameTouched(true);
    };

    const handleBirthDateChange = (newValue: Moment | null) => {
        setBirthDate(newValue);
        if (!birthDateTouched) setBirthDateTouched(true);
    };

    const handleSchoolChange = (
        _: React.SyntheticEvent,
        newValue: School | string | null
    ) => {
        if (typeof newValue === 'string') {
            setSchool(newValue);
        } else {
            setSchool(newValue?.name ?? '');
        }
        if (!schoolTouched) setSchoolTouched(true);
    };

    const handleSelectedProgramChange = (
        _: React.SyntheticEvent,
        newValue: Program | null
    ) => {
        setSelectedProgram(newValue);

        if (!selectedProgramTouched) setSelectedProgramTouched(true);
    };

    const handleTogglePassword = () => setShowPassword((prev) => !prev);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (!passwordTouched) setPasswordTouched(true);
    };

    const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
        if (!confirmPasswordTouched) setConfirmPasswordTouched(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFirstNameTouched(true);
        setLastNameTouched(true);
        setBirthDateTouched(true);
        setSchoolTouched(true);
        setSelectedProgramTouched(true);
        setPasswordTouched(true);
        setConfirmPasswordTouched(true);

        if (!school) {
            pushNotification({
                details: 'School is required',
                type: NotificationTypes.Error,
            });
            return;
        }

        if (!selectedProgram) {
            pushNotification({
                details: 'Program is required',
                type: NotificationTypes.Error,
            });
            return;
        }

        if (!firstName || !lastName || !birthDate || !password) {
            return;
        }
        setFormDisabled(true);
        try {
            const request = {
                firstName,
                lastName,
                birthDate: birthDate.format("YYYY-MM-DD"),
                school,
                programId: selectedProgram?._id,
                password,
                isEula: true
            };

            if (selectedProgram) {
                request.programId = selectedProgram._id;
            }
            const updateProfileResponse = await updateProfile(request);
            pushNotification({
                details: updateProfileResponse.message,
                type: NotificationTypes.Success,
            });
            navigate("/");
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            pushNotification({
                details: errorMessage,
                type: NotificationTypes.Error,
            });
        } finally {
            setFormDisabled(false);
        }
    };

    const filter = createFilterOptions<any>();

    return (
        <PinnacleFormContainer
            formName={COMPLETE_PROFILE}
            hideLogo={true}
        >
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <form onSubmit={handleSubmit} noValidate>
                    <Box className={styles["form-panel-box"]}>

                        {/* FIRST NAME */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <PinnacleTextField
                                error={firstNameTouched && !isFirstNameValid}
                                color={firstNameTouched && !isFirstNameValid ? 'error' : 'primary'}
                                helperText={firstNameTouched && !isFirstNameValid ? E_REQUIRED_FIRST_NAME : ''}
                                id="firstName"
                                label="First name"
                                name="firstName"
                                variant="outlined"
                                autoFocus
                                onChange={handleFirstNameChange}
                                onBlur={() => setFirstNameTouched(true)}
                                value={firstName}
                                autoComplete="first-name"
                                required
                                disabled={formDisabled}
                            />
                        </FieldSkeletonWrapper>

                        {/* LAST NAME */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <PinnacleTextField
                                error={lastNameTouched && !isLastNameValid}
                                color={lastNameTouched && !isLastNameValid ? 'error' : 'primary'}
                                helperText={lastNameTouched && !isLastNameValid ? E_REQUIRED_LAST_NAME : ''}
                                id="lastName"
                                name="lastName"
                                label="Last name"
                                variant="outlined"
                                onChange={handleLastNameChange}
                                onBlur={() => setLastNameTouched(true)}
                                value={lastName}
                                autoComplete="last-name"
                                required
                                disabled={formDisabled}
                            />
                        </FieldSkeletonWrapper>

                        {/* BIRTHDATE */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <StyledPinnacleDatePicker
                                label="Birth Date"
                                value={birthDate}
                                onChange={(newValue) => handleBirthDateChange(newValue)}
                                slots={{
                                    openPickerIcon: () => <CalendarMonthIcon sx={{ color: theme.palette.common.white }} />,
                                }}
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        required: true,
                                        error: birthDateTouched && !isBirthDateValid,
                                        helperText: birthDateTouched && !isBirthDateValid ? E_REQUIRED_BIRTHDATE : '',
                                        onBlur: () => setBirthDateTouched(true),
                                        disabled: formDisabled,
                                        fullWidth: true
                                    },
                                }}
                                maxDate={maxAllowedDate}
                                disableFuture
                            />
                        </FieldSkeletonWrapper>

                        {/* EMAIL */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <PinnacleTextField
                                id="email"
                                name="email"
                                label="Email address"
                                variant="outlined"
                                value={email}
                                disabled={true}
                            />
                        </FieldSkeletonWrapper>

                        {/* SCHOOL */}
                        <FieldSkeletonWrapper loading={profileLoading || schoolsLoading}>
                            <Box sx={{ mt: 0.75, mb: 0.75 }}>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 1,
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        fontSize: '12px',
                                        color: 'rgba(255,255,255,0.75)',
                                    }}
                                >
                                    Type your school name if it is not in the list.
                                </Box>
                            </Box>
                            <PinnacleAutocomplete
                                id="school"
                                label="School"
                                options={schoolsOption ?? []}
                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                disabled={formDisabled}
                                placeholder={CHOOSE_SCHOOL}
                                renderInput={(params) => (
                                    <PinnacleTextField
                                        autoComplete="new-school"
                                        {...params}
                                        label="School"
                                        variant="outlined"
                                        name="school-field"
                                        type="text"
                                        onBlur={() => setSchoolTouched(true)}
                                        value={school}
                                    />
                                )}
                                onChange={(_, newValue) => {
                                    if (typeof newValue === 'string') {
                                        handleSchoolChange(_, newValue)
                                    } else if (newValue) {
                                        // Create a new value from the user input
                                        handleSchoolChange(_, newValue)
                                    } else {
                                        handleSchoolChange(_, newValue)
                                    }
                                }}
                                filterOptions={(options: any, params: any) => {
                                    const filtered = filter(options, params);

                                    const { inputValue } = params;
                                    // Suggest the creation of a new value
                                    const isExisting = options.some((option: any) => inputValue === option.name);
                                    if (inputValue !== '' && !isExisting) {
                                        filtered.push({
                                            inputValue,
                                            name: inputValue //`Select school: "${inputValue}"`,
                                        });
                                    }

                                    return filtered;
                                }}
                                getOptionLabel={(option: any) => {
                                    // Value selected with enter, right from the input
                                    if (typeof option === 'string') {
                                        return option;
                                    }
                                    // Add "xxx" option created dynamically
                                    if (option.inputValue) {
                                        return option.inputValue;
                                    }
                                    // Regular option
                                    return option.name ?? '';
                                }}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <li key={key} {...optionProps}>
                                            {option.name}
                                        </li>
                                    );
                                }}
                            />
                        </FieldSkeletonWrapper>


                        {/* PROGRAM */}
                        {!currentProfile?.user?.programId && (
                            <FieldSkeletonWrapper loading={programsLoading || profileLoading}>
                                <PinnacleAutocomplete
                                    label="Programs"
                                    options={programsOption ?? []}
                                    getOptionLabel={(option) => option.name ?? ''}
                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                    value={selectedProgram}
                                    onChange={(_, newValue) => handleSelectedProgramChange(_, newValue)}
                                    placeholder={CHOOSE_PROGRAM}
                                    disabled={formDisabled || programsLoading}
                                    loading={programsLoading}
                                    loadingText="Loading programs..."
                                    renderInput={(params) => (
                                        <PinnacleTextField
                                            {...params}
                                            autoComplete="new-program"
                                            label="Programs"
                                            variant="outlined"
                                            name="programs"
                                            onBlur={() => setSelectedProgramTouched(true)}
                                            value={selectedProgram}
                                        />
                                    )}
                                />
                            </FieldSkeletonWrapper>
                        )}

                        {/* PASSWORD */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <PinnacleTextField
                                error={passwordTouched && (!isPasswordValid)}
                                color={passwordTouched && (!isPasswordValid) ? 'error' : 'primary'}
                                helperText={(passwordTouched && !isPasswordValid) && E_ENTER_PASSWORD}
                                id="set-password"
                                label="Password"
                                variant="outlined"
                                name="set-password"
                                onChange={handlePasswordChange}
                                onBlur={() => setPasswordTouched(true)}
                                value={password}
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="off"
                                disabled={formDisabled}
                                slotProps={{
                                    input: {
                                        endAdornment: <PasswordAdornment show={showPassword} toggleShow={handleTogglePassword} />,
                                    },
                                }}
                            />
                        </FieldSkeletonWrapper>

                        {/* CONFIRM PASSWORD */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <PinnacleTextField
                                error={
                                    (confirmPasswordTouched && (!isConfirmPasswordValid)) ||
                                    password !== confirmPassword
                                }
                                color={
                                    ((confirmPasswordTouched && (!isConfirmPasswordValid)) ||
                                        password !== confirmPassword)
                                        ? 'error'
                                        : 'primary'
                                }
                                helperText={
                                    (confirmPasswordTouched && !isConfirmPasswordValid)
                                        ? E_CONFIRM_PASSWORD
                                        : (password !== confirmPassword)
                                            ? E_PASSWORD_NOT_MATCHED
                                            : ''
                                }
                                id="confirmNewPassword"
                                label="Confirm New Password"
                                variant="outlined"
                                onChange={handleConfirmPasswordChange}
                                onBlur={() => setConfirmPasswordTouched(true)}
                                value={confirmPassword}
                                type={showConfirmPassword ? 'text' : 'password'}
                                autoComplete="current-confirm-password"
                                disabled={formDisabled}
                                slotProps={{
                                    input: {
                                        endAdornment: <PasswordAdornment show={showConfirmPassword} toggleShow={handleToggleConfirmPassword} />,
                                    },
                                }}
                            />
                        </FieldSkeletonWrapper>

                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={!firstName || !lastName || !birthDate || !password || !confirmPassword || formDisabled}
                            sx={{
                                mb: 1.5,
                                '&.Mui-disabled': {
                                    backgroundColor: grey[800],
                                    color: grey[400],
                                    opacity: 1,
                                },
                            }}
                        >
                            {formDisabled ? (
                                <CircularProgress size={24} sx={{ color: blue[300] }} />
                            ) : (
                                BTN_DONE
                            )}
                        </Button>
                    </Box>
                </form>
            </LocalizationProvider>
        </PinnacleFormContainer>

    );
}